import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { type ApiError, api, session, unwrap } from '../api/client';
import { usePlans } from '../api/hooks';
import type { Billing, CheckoutResult } from '../api/types';
import { useSession } from '../auth/session';
import { LegalLinks } from '../components/PublicLayout';
import { Card, ErrorAlert, Field } from '../components/ui';

interface AuthResult {
  token: string;
  role: string;
  verificationRequired?: boolean;
  devCode?: string;
}

function Shell({ step, children }: { step: number; children: React.ReactNode }) {
  return (
    <div className="auth-shell">
      <Card className="auth-card">
        <div className="brand" style={{ padding: 0, marginBottom: 12 }}>
          <span className="brand-mark">A</span> AutoDM
        </div>
        <div className="steps" aria-label="Signup progress">
          {[1, 2, 3, 4].map((n) => (
            <span key={n} className={n <= step ? 'done' : ''} />
          ))}
        </div>
        {children}
      </Card>
    </div>
  );
}

export function SignupPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState({ email: '', password: '', workspaceName: '' });
  const m = useMutation({
    mutationFn: () => unwrap(api.post<AuthResult>('/auth/signup', form)),
    onSuccess: (r) => {
      session.set(r.token);
      void qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/verify', { state: { email: form.email, devCode: r.devCode } });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    m.mutate();
  };
  return (
    <Shell step={1}>
      <h1>Create your account</h1>
      <p className="muted">
        Start with your login details. You will verify your email, pick a plan, then connect
        Instagram from Social Accounts.
      </p>
      <form onSubmit={submit}>
        <ErrorAlert error={m.error as ApiError | null} />
        <Field label="Work email">
          <input
            className="input"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field label="Password" help="At least 8 characters">
          <input
            className="input"
            type="password"
            name="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </Field>
        <Field label="Brand or workspace name">
          <input
            className="input"
            value={form.workspaceName}
            onChange={(e) => setForm({ ...form, workspaceName: e.target.value })}
          />
        </Field>
        <button className="btn btn-primary" type="submit" disabled={m.isPending}>
          Continue
        </button>
      </form>
      <p className="muted small" style={{ marginTop: 14 }}>
        Already have an account? <Link to="/login">Log in</Link>
      </p>
      <LegalLinks />
    </Shell>
  );
}

export function VerifyPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const state = (useLocation().state ?? {}) as { email?: string; devCode?: string };
  const [email, setEmail] = useState(state.email ?? '');
  const [code, setCode] = useState('');
  const verify = useMutation({
    mutationFn: () => unwrap(api.post<AuthResult>('/auth/verify', { email, code })),
    onSuccess: async (r) => {
      session.set(r.token);
      await qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/onboarding');
    },
  });
  const resend = useMutation({ mutationFn: () => unwrap(api.post('/auth/resend', { email })) });
  return (
    <Shell step={2}>
      <h1>Check your email</h1>
      <p className="muted">We sent a 6-digit code to confirm your address.</p>
      {state.devCode ? (
        <div className="alert alert-info">
          Development mode: your code is <strong data-testid="dev-code">{state.devCode}</strong>
        </div>
      ) : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          verify.mutate();
        }}
      >
        <ErrorAlert error={verify.error as ApiError | null} />
        <Field label="Email">
          <input
            className="input"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Verification code">
          <input
            className="input mono"
            inputMode="numeric"
            name="code"
            autoComplete="one-time-code"
            pattern="\d{6}"
            required
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </Field>
        <div className="actions">
          <button className="btn btn-primary" type="submit" disabled={verify.isPending}>
            Verify
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => resend.mutate()}
            disabled={resend.isPending}
          >
            {resend.isSuccess ? 'Code sent' : 'Resend code'}
          </button>
        </div>
        {/* UI-51: a refused resend (three an hour) is said */}
        <ErrorAlert error={resend.error as ApiError | null} />
      </form>
    </Shell>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState({ email: '', password: '' });
  const m = useMutation({
    mutationFn: () => unwrap(api.post<AuthResult>('/auth/login', form)),
    onSuccess: async (r) => {
      session.set(r.token);
      await qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/app');
    },
  });
  const err = m.error as ApiError | null;
  return (
    <div className="auth-shell">
      <Card className="auth-card">
        <div className="brand" style={{ padding: 0, marginBottom: 12 }}>
          <span className="brand-mark">A</span> AutoDM
        </div>
        <h1>Welcome back</h1>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            m.mutate();
          }}
        >
          <ErrorAlert error={err} />
          {err?.code === 'email_unverified' ? (
            <p className="small">
              <Link to="/verify" state={{ email: form.email }}>
                Verify your email
              </Link>{' '}
              to continue.
            </p>
          ) : null}
          <Field label="Email">
            <input
              className="input"
              type="email"
              name="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label="Password">
            <input
              className="input"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </Field>
          <button className="btn btn-primary" type="submit" disabled={m.isPending}>
            Log in
          </button>
        </form>
        <p className="muted small" style={{ marginTop: 14 }}>
          New here? <Link to="/signup">Start a free trial</Link> ·{' '}
          <Link to="/forgot">Forgot password?</Link>
        </p>
        <LegalLinks />
      </Card>
    </div>
  );
}

const TOOLS = ['ManyChat', 'InstantDM', 'MobileMonkey', 'Chatfuel', 'PostRoutine', 'None of these'];
const PROFILES = [
  ['influencer', 'Influencer'],
  ['brand', 'Brand'],
  ['creator', 'Creator'],
  ['agency', 'Agency'],
  ['online_store', 'Online store'],
  ['offline_store', 'Offline store'],
] as const;

export function OnboardingPage() {
  const navigate = useNavigate();
  const { me, refresh } = useSession();
  const [tools, setTools] = useState<readonly string[]>([]);
  const [profile, setProfile] = useState<string>('brand');
  const m = useMutation({
    mutationFn: () =>
      unwrap(api.post('/portal/onboarding', { toolsUsed: tools, profileType: profile })),
    onSuccess: async () => {
      await refresh();
      navigate('/plans');
    },
  });
  if (me && !me.emailVerified) navigate('/verify');
  return (
    <Shell step={3}>
      <h1>Tell us about you</h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorAlert error={m.error as ApiError | null} />
        <Field label="Have you used any of these tools before?" group>
          <div className="pill-row">
            {TOOLS.map((t) => (
              <label key={t} className={`btn btn-sm ${tools.includes(t) ? 'btn-primary' : ''}`}>
                <input
                  type="checkbox"
                  className="pill-input"
                  checked={tools.includes(t)}
                  onChange={() =>
                    setTools(tools.includes(t) ? tools.filter((x) => x !== t) : [...tools, t])
                  }
                />
                {t}
              </label>
            ))}
          </div>
        </Field>
        <Field label="What suits your profile the best?">
          <select className="input" value={profile} onChange={(e) => setProfile(e.target.value)}>
            {PROFILES.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </Field>
        <button className="btn btn-primary" type="submit" disabled={m.isPending}>
          Continue
        </button>
      </form>
    </Shell>
  );
}

export function PlansPage() {
  const navigate = useNavigate();
  const { me, refresh } = useSession();
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('yearly');
  const plans = usePlans();
  const billing = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: () => unwrap(api.get<Billing>('/portal/billing')),
  });
  const m = useMutation({
    mutationFn: (plan: string) =>
      unwrap(api.post<CheckoutResult>('/portal/billing/checkout', { plan, interval })),
    onSuccess: async (r) => {
      if (r.redirectUrl) {
        window.location.assign(r.redirectUrl);
        return;
      }
      await refresh();
      navigate('/app');
    },
  });
  // UI-53: a plan is chosen by a verified mailbox (BIL-42); unverified users verify first
  if (me && !me.emailVerified) navigate('/verify');
  return (
    <div className="auth-shell">
      <div style={{ width: 'min(1100px, 100%)' }}>
        <div className="steps" style={{ maxWidth: 440, margin: '0 auto 18px' }}>
          {[1, 2, 3, 4].map((n) => (
            <span key={n} className="done" />
          ))}
        </div>
        <h1 style={{ textAlign: 'center' }}>Choose your plan</h1>
        <p className="muted" style={{ textAlign: 'center' }}>
          You need an active subscription to run automations. When a card is needed you are taken to
          the payment page; the free trial needs none.
        </p>
        <div className="actions" style={{ justifyContent: 'center', marginBottom: 16 }}>
          <button
            type="button"
            className={`btn ${interval === 'monthly' ? 'btn-primary' : ''}`}
            onClick={() => setInterval('monthly')}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`btn ${interval === 'yearly' ? 'btn-primary' : ''}`}
            onClick={() => setInterval('yearly')}
          >
            Yearly (save up to 17%)
          </button>
        </div>
        <ErrorAlert error={m.error as ApiError | null} />
        <div className="grid grid-4">
          {(plans.data?.items ?? []).map((p) => (
            <div className={`card plan-card ${p.plan === 'legend' ? 'featured' : ''}`} key={p.plan}>
              <div className="card-title">{p.name}</div>
              {p.plan === 'trial' ? (
                <div className="price">
                  Free<small> for 7 days</small>
                </div>
              ) : (
                <div className="price">
                  ${interval === 'yearly' ? p.yearlyUsdPerMonth : p.monthlyUsd}
                  <small>/month</small>
                </div>
              )}
              <ul className="features-list">
                {p.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              {p.plan === 'trial' && billing.data?.trialAvailable === false ? (
                // BIL-44: one trial per workspace and per mailbox; say so instead of offering a 409
                <p className="muted" style={{ marginTop: 14 }}>
                  Your free trial has been used.
                </p>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ marginTop: 14 }}
                  disabled={m.isPending}
                  onClick={() => m.mutate(p.plan)}
                >
                  {p.plan === 'trial' ? 'Start free trial' : `Subscribe to ${p.name}`}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** UI-11: the hosted checkout sends the browser back here; the webhook may land a moment later. */
export function BillingReturnPage() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const billing = useQuery({
    queryKey: ['billing', 'return'],
    queryFn: () => unwrap(api.get<Billing>('/portal/billing')),
    refetchInterval: (q) => (q.state.data?.usable ? false : 2000),
  });
  const usable = billing.data?.usable === true;
  useEffect(() => {
    if (!usable) return;
    void refresh().then(() => navigate('/app', { replace: true }));
  }, [usable, refresh, navigate]);
  return (
    <div className="auth-shell">
      <Card title="Finishing your subscription">
        {usable ? (
          <p>Payment confirmed. Taking you to the dashboard…</p>
        ) : (
          <p className="muted">
            Waiting for the payment confirmation from your provider. This page refreshes itself; it
            usually takes a few seconds.
          </p>
        )}
        <ErrorAlert error={billing.error as ApiError | null} />
        <Link to="/plans" className="btn" style={{ marginTop: 12 }}>
          Back to plans
        </Link>
      </Card>
    </div>
  );
}

export function BillingCancelledPage() {
  return (
    <div className="auth-shell">
      <Card title="Checkout cancelled">
        <p className="muted">No payment was taken. Pick a plan whenever you are ready.</p>
        <Link to="/plans" className="btn btn-primary" style={{ marginTop: 12 }}>
          Choose a plan
        </Link>
      </Card>
    </div>
  );
}
